/**
 * The default NAME a new deal or quote starts with.
 *
 * The owner's pattern: `<company short name> - Plans - <current cycle>`, and
 * it is a **starting point, not a rule** — every field it fills stays editable,
 * and nothing here ever overwrites something a person typed.
 *
 * ## Why one module and not two
 *
 * A deal's `dealName` and a quote's `subject` are different columns on
 * different tables, but they are now one naming convention. Two copies would
 * agree on the day they were written and drift the first time either was
 * edited — the same reason `src/lib/inclusions.ts` is one module rather than
 * four call sites. If the pattern changes, it changes here.
 *
 * ## The three segments, and what each does when it is missing
 *
 * **The company** comes from {@link companyDocumentName}, so it is the short
 * name where somebody has set one and the full name otherwise. ⚠️ That
 * fallback is the NORMAL path today, not an edge case: of 148 companies, only
 * a handful carry a `shortName`, and Xero has no equivalent field to backfill
 * the rest from. So nearly every generated name still
 * carries the long form —
 * `University of Oxford - Saïd Business School - Plans - FY25/26`. That is
 * correct, not a bug, and it is exactly the argument for filling short names
 * in. Do not "fix" it by truncating: a truncated school name is a different
 * school.
 *
 * **`Plans`** is a literal, spelled once, here. It is the owner's word.
 *
 * **The cycle** is the one whose window contains today — see
 * {@link currentCycle}. There may be none, and that is an ordinary state
 * rather than a fault.
 *
 * ⚠️ **A missing segment is DROPPED, never rendered as a placeholder or an
 * empty gap.** `Acme -  - ` reads as broken and invites somebody to "fix" it
 * by typing into a name that is only a default. With no company and no cycle
 * this returns `''`, and the caller leaves the field alone — an empty box a
 * person fills in is honest; a half-built string is not.
 */
import { companyDocumentName } from './format';
import type { Cycle } from './types';

/** The owner's word for the middle segment. One spelling, in one place. */
export const PLANS_SEGMENT = 'Plans';

/** ` - `, matching the separator already used inside the imported names. */
const JOIN = ' - ';

/**
 * Today as `YYYY-MM-DD`, built from LOCAL calendar parts.
 *
 * ⚠️ **Never `toISOString().slice(0, 10)`.** That is UTC, so west of Greenwich
 * it returns yesterday for most of the evening — which would pick the wrong
 * cycle on exactly the days a cycle boundary falls. Cycle dates are plain
 * `YYYY-MM-DD` with no timezone and are compared as strings, so the value they
 * are compared against has to be built the same way.
 */
export function todayLocal(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The cycle whose window contains today, or `null` when none does.
 *
 * Both bounds are INCLUSIVE — a cycle's first and last day belong to it.
 * Compared as strings, never as `Date`s: `new Date('2026-05-31')` is midnight
 * UTC and renders as 30 May west of Greenwich, and `YYYY-MM-DD` sorts
 * correctly as text anyway.
 *
 * ⚠️ **Returns `null` rather than guessing** when today falls in no cycle —
 * between two business years, or before the first has been set up. The
 * alternatives are all worse: the nearest cycle names a period this deal is
 * not in, and the newest row names whichever was created last, which is not a
 * date at all. A name with no cycle segment is honest; a name carrying the
 * wrong year is a document somebody files under the wrong period.
 *
 * If more than one cycle contains today — overlapping windows are not refused
 * by the API — the FIRST in the list wins, deterministically, rather than the
 * shortest or the newest. Overlap is a data problem to fix in the catalog, and
 * a client picking a winner by some cleverer rule would hide it.
 */
export function currentCycle(cycles: readonly Cycle[], today = todayLocal()): Cycle | null {
  return cycles.find((cycle) => cycle.startDate <= today && today <= cycle.endDate) ?? null;
}

/**
 * The default name for a new deal or quote raised against `company`.
 *
 * Returns `''` when there is nothing to build from, which the caller must read
 * as "leave the field alone" rather than as a value to write.
 *
 * ⚠️ **Only ever for a CREATE, and only while the field is untouched.** A
 * stored name is the record's own: re-deriving it because the cycle rolled
 * over, or because somebody set a short name last week, would rename a
 * document that has already been sent to a customer. The caller owns that
 * guard — this function has no idea which it is being called for.
 */
export function defaultDocumentName(
  company: { name: string; shortName: string | null } | null,
  cycle: Cycle | null,
): string {
  const segments = [
    company ? companyDocumentName(company).trim() : '',
    PLANS_SEGMENT,
    cycle ? cycle.cycleName.trim() : '',
  ].filter((segment) => segment !== '');

  /*
   * `Plans` alone is not a name — it says nothing about which account or
   * period, and it would be the same string on every deal in the system. With
   * no company we would rather hand back nothing and let the person type.
   */
  if (!company) return '';

  return segments.join(JOIN);
}
